import React, { useEffect, useState, useRef, useCallback } from "react";
import { fetchActivePlans } from "../../services/planService";
import { fetchActiveSpeakers } from "../../services/adminSpeakerServices";
import { addSessionToDay, updateSession } from "../../services/adminTimeTableServices";
import { extractLocalTime, buildLocalDateTime } from "../../../utils/dateTimeUtils";
import { searchPassages } from "../../blog/reading/adminReadingService";

const inputStyle = {
 width: "100%",
 padding: "8px",
 marginBottom: "12px",
};

const SessionModal = ({ dayId, dayDate, session, onClose, reload }) => {
 const [plans, setPlans] = useState([]);
 const [speakers, setSpeakers] = useState([]);

 const [form, setForm] = useState({
 topic: "",
 level: "",
 planId: "",
 totalSlots: 1,
 speakerId: "",
 readingPassageId: null,
 });

 const [startClock, setStartClock] = useState("");
 const [endClock, setEndClock] = useState("");
 const [selectedPassage, setSelectedPassage] = useState(null);
 const [passageQuery, setPassageQuery] = useState("");
 const [passageResults, setPassageResults] = useState([]);
 const [passageOpen, setPassageOpen] = useState(false);
 const debounceRef = useRef(null);
 const passageWrapRef = useRef(null);

 const handlePassageSearch = useCallback((q) => {
 clearTimeout(debounceRef.current);
 if (!q.trim()) { setPassageResults([]); setPassageOpen(false); return; }
 debounceRef.current = setTimeout(async () => {
 const res = await searchPassages(q);
 if (res.status === 200 && Array.isArray(res.data)) {
 setPassageResults(res.data);
 setPassageOpen(true);
 }
 }, 300);
 }, []);

 useEffect(() => { handlePassageSearch(passageQuery); }, [passageQuery, handlePassageSearch]);

 useEffect(() => {
 const handler = (e) => { if (passageWrapRef.current && !passageWrapRef.current.contains(e.target)) setPassageOpen(false); };
 document.addEventListener("mousedown", handler);
 return () => document.removeEventListener("mousedown", handler);
 }, []);

 /* ===============================
 Fetch plans & speakers
 =============================== */
 useEffect(() => {
 fetchActivePlans().then((res) => setPlans(res.data || []));
 fetchActiveSpeakers().then((res) => setSpeakers(res.data || []));

 if (session) {
 setForm({
 topic: session.topic,
 level: session.level,
 planId: session.planId,
 totalSlots: session.totalSlots,
 speakerId: session.speakerId ?? "",
 readingPassageId: session.readingPassageId ?? null,
 });
 if (session.readingPassageId && session.readingPassage) {
 setSelectedPassage({
 id: session.readingPassageId,
 label: session.readingPassage.title,
 subLabel: `${session.readingPassage.difficulty} · ${session.readingPassage.category}`,
 });
 }
 const localStart = extractLocalTime(session.startTime);
 const localEnd = extractLocalTime(session.endTime);
 setStartClock(localStart);
 setEndClock(localEnd);
 }
 }, [session]);

 /* ===============================
 Submit
 =============================== */
 const handleSubmit = async () => {
 const startDateTime = buildLocalDateTime(dayDate, startClock);
 const endDateTime = buildLocalDateTime(dayDate, endClock);

 const payload = {
 ...form,
 speakerId: form.speakerId || null,
 readingPassageId: form.readingPassageId || null,
 startTime: startDateTime,
 endTime: endDateTime,
 };


 try {
 if (session) {
 await updateSession(session.id, payload);
 } else {
 await addSessionToDay(dayId, payload);
 }

 reload();
 onClose();
 } catch (err) {
 alert(err.response?.data?.message || "Validation error");
 }
 };

 return (
 <div
 style={{
 position: "fixed",
 inset: 0,
 background: "rgba(0,0,0,0.5)",
 display: "flex",
 justifyContent: "center",
 alignItems: "center",
 zIndex: 1000,
 }}
 onClick={onClose}
 >
 <div
 style={{
 background: "#fff",
 padding: "20px",
 borderRadius: "8px",
 width: "400px",
 }}
 onClick={(e) => e.stopPropagation()}
 >
 <h3>{session ? "Edit Session" : "Add Session"}</h3>

 {/* Date */}
 <input style={{ ...inputStyle, background: "#eee" }} value={dayDate} disabled />

 {/* Topic */}
 <input
 style={inputStyle}
 placeholder="Topic"
 value={form.topic}
 onChange={(e) => setForm({ ...form, topic: e.target.value })}
 />

 {/* Level */}
 <select
 style={inputStyle}
 value={form.level}
 onChange={(e) => setForm({ ...form, level: e.target.value })}
 >
 <option value="">Select Level</option>
 <option value="BEGINNER">BEGINNER</option>
 <option value="INTERMEDIATE">INTERMEDIATE</option>
 <option value="ADVANCED">ADVANCED</option>
 </select>

 {/* Time */}
 <input
 type="time"
 style={inputStyle}
 value={startClock}
 onChange={(e) => {
 const start = e.target.value;
 setStartClock(start);

 const [h, m] = start.split(":").map(Number);
 const end = new Date(0, 0, 0, h, m + 30);

 const endVal = `${String(end.getHours()).padStart(2, "0")}:${String(
 end.getMinutes()
 ).padStart(2, "0")}`;

 setEndClock(endVal);
 }}
 />

 <input
 type="time"
 style={inputStyle}
 value={endClock}
 onChange={(e) => {
 setEndClock(e.target.value);
 }}
 />

 {/* Plan */}
 <select
 style={inputStyle}
 value={form.planId}
 onChange={(e) => setForm({ ...form, planId: Number(e.target.value) })}
 >
 <option value="">Select Plan</option>
 {plans.map((p) => (
 <option key={p.id} value={p.id}>
 {p.planName} ({p.planType})
 </option>
 ))}
 </select>

 {/* Slots */}
 <input
 type="number"
 min={1}
 style={inputStyle}
 value={form.totalSlots}
 onChange={(e) =>
 setForm({ ...form, totalSlots: Number(e.target.value) })
 }
 />

 {/* ✅ Speaker */}
 <select
 style={inputStyle}
 value={form.speakerId}
 onChange={(e) =>
 setForm({
 ...form,
 speakerId: e.target.value ? Number(e.target.value) : "",
 })
 }
 >
 <option value="">Select Speaker</option>
 {speakers.map((s) => (
 <option key={s.id} value={s.id}>
 {s.name}
 </option>
 ))}
 </select>

 {/* Reading Passage (optional) */}
 <div ref={passageWrapRef} style={{ marginBottom: 12 }}>
 <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#444" }}>Reading Passage (optional)</div>
 {selectedPassage ? (
 <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#e0f7fa", borderRadius: 8, padding: "6px 12px" }}>
 <span style={{ fontSize: 13, color: "#00796b", flex: 1 }}>
 📖 {selectedPassage.label} — {selectedPassage.subLabel}
 </span>
 <button
 type="button"
 onClick={() => { setSelectedPassage(null); setForm((f) => ({ ...f, readingPassageId: null })); }}
 style={{ background: "none", border: "none", cursor: "pointer", color: "#00796b", fontWeight: 700, fontSize: 16, lineHeight: 1 }}
 >✕</button>
 </div>
 ) : (
 <div style={{ position: "relative" }}>
 <input
 style={inputStyle}
 placeholder="Search passage..."
 value={passageQuery}
 onChange={(e) => setPassageQuery(e.target.value)}
 />
 {passageOpen && passageResults.length > 0 && (
 <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
 {passageResults.map((r) => (
 <div
 key={r.id}
 onClick={() => {
 setSelectedPassage(r);
 setForm((f) => ({ ...f, readingPassageId: r.id }));
 setPassageQuery("");
 setPassageOpen(false);
 }}
 style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
 onMouseEnter={(e) => e.currentTarget.style.background = "#f0fafa"}
 onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
 >
 <div style={{ fontWeight: 600, fontSize: 14 }}>{r.label}</div>
 {r.subLabel && <div style={{ fontSize: 12, color: "#888" }}>{r.subLabel}</div>}
 </div>
 ))}
 </div>
 )}
 </div>
 )}
 </div>

 {/* Actions */}
 <div style={{ textAlign: "right" }}>
 <button onClick={handleSubmit} style={{ marginRight: "10px" }}>
 Save
 </button>
 <button onClick={onClose}>Cancel</button>
 </div>
 </div>
 </div>
 );
};

export default SessionModal;
